import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { AlertColor, Button, Divider, Grid, Modal, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import strings from '../../../common/Translation/Translate';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import Form, { FormDataModel } from '../../../components/Form';
import { formatTimeForApi, parseTimeValue } from 'components/TimeInput/TimeInput';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import {
  eventTypeList,
  searchFormData,
  selectedOrgForCosting,
  setEventTypeList,
  setSearchDataSliceInitState,
  setSearchFormData,
  setSearchedData,
  setVenueList,
} from '../../../utils/redux/reducer/searched-data-slice';
import { removeFromStorage } from '../../../utils/storage';
import { createDropdownData, removeErrorFieldsFromValues } from '../../../utils/validators/HelperFunctions';
import { SearchBarForm } from './SearchBarForm';
import { Box } from '@mui/system';
import { flushSync } from 'react-dom';
import { defaultLanguage } from 'utils/redux/reducer/authentication-slice';
import PostAddIcon from '@mui/icons-material/PostAdd';
import axiosInstance from 'utils/axios';
import { setSelectedVenuesForCosting } from 'utils/redux/reducer/costing-slice';

const SearchBar: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lang = useSelector(defaultLanguage);
  let formRef: RefObject<Form | null | undefined> = useRef();
  const [hasError, setHasError] = useState<boolean>(false);
  const [locations, setLoctions] = useState<any>([]);
  const eventTypes = useSelector(eventTypeList);

  const [selectedLocations, setSelectedLocations] = useState<any>([]);
  const [loading, setLoading] = useState<any>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const theme = useTheme();
  const screenSizeDownLg = useMediaQuery(theme.breakpoints.down(1261));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchData, setSearchData] = useState<any>({});
  const [selectOrg, setSelectOrg] = useState<any>('');
  const location = useLocation();
  const [isLocationDisabled, setIsLocationDisabled] = useState<boolean>(false);
  const searchFormDataStorage = useSelector(searchFormData);
  const selectedOrgStorage = useSelector(selectedOrgForCosting);

  useEffect(() => {
    if (location.pathname === routes.availableVenues) {
      setIsLocationDisabled(true);
    }
  }, []);

  useEffect(() => {
    setSearchData({
      ...searchFormDataStorage,
      startTime: dayjs(searchFormDataStorage.startTime, 'HH:mm'),
      endTime: dayjs(searchFormDataStorage.endTime, 'HH:mm'),
    });
    setSelectOrg(selectedOrgStorage);
    const array = searchFormDataStorage?.location?.map((item: any) => item.value);
    setSelectedLocations(array);
  }, [searchFormDataStorage, selectedOrgStorage]);

  const getEventTypeList = useCallback(async () => {
    try {
      setLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getEventTypeList);
      if (status === 200) {
        setLoading(false);
        const customData = createDropdownData(data, ['id', 'name']);
        dispatch(setEventTypeList(customData));
      }
    } catch (error: any) {
      handleCatch(error, 'error');
    }
  }, []);

  const getLocationList = useCallback(async () => {
    try {
      setLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getLocationList);
      if (status === 200) {
        setLoading(false);
        const customData = createDropdownData(data, ['id', 'name']);
        setLoctions(customData);
      }
    } catch (error: any) {
      handleCatch(error, 'error');
    }
  }, []);

  useEffect(() => {
    (async () => {
      getEventTypeList();
      getLocationList();
    })();
  }, [getEventTypeList, getLocationList, lang]);

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleCatch = (error: any, severity: AlertColor) => {
    setLoading(false);
    setMessage({
      display: true,
      severity: severity,
      message: error.response.data.message,
    });
  };

  const handleChange = (field: string, value: any) => {
    if (field === 'location') {
      const array = value.map((item: any) => item.value);
      setSelectedLocations(array);
    }
    if (field === 'startDate') {
      if (dayjs(value).isValid()) formRef.current?.handleChange(value, 'endDate', { error: false, message: '' });
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    try {
      const { getFormData } = formRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };
      const { formData, isFormValid } = getFormData();
      const body = removeErrorFieldsFromValues(formData);
      setHasError(false);
      if (isFormValid && dayjs(body.startDate as string).isValid() && dayjs(body.endDate as string).isValid()) {
        if (dayjs(body.startDate as string).isAfter(dayjs(body.endDate as string))) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.datePickerValidation,
          });
        } else if (
          (() => {
            const start = parseTimeValue(body.startTime as string);
            const end = parseTimeValue(body.endTime as string);
            return start && end && (start.isSame(end) || start.isAfter(end));
          })()
        ) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.timePickerValidation,
          });
        } else {
          setLoading(true);
          const requestBody = {
            location: selectedLocations,
            eventType: body.eventType,
            bookingStartDate: dayjs(body.startDate as string).format('DD-MM-YYYY'),
            bookingEndDate: dayjs(body.endDate as string).format('DD-MM-YYYY'),

            startTime: formatTimeForApi(body.startTime as string),
            endTime: formatTimeForApi(body.endTime as string),
            guest: body.guests,
            layouts: [],
          };
          let url;
          if (location.pathname === routes.available_venues) {
            url = URLS.getAvailableVenues(selectOrg);
          } else {
            url = URLS.getSearchedOrganisations;
          }
          const { status, data } = await axiosInstance.post(url, requestBody);
          if (status === 200 || status === 201) {
            setLoading(false);
            if (data.length === 0) {
              setMessage({
                display: true,
                severity: 'error',
                message: strings.no_venue_Found_error_message,
              });
            } else {
              setOpenModal(false);
              if (location.pathname.includes(routes.bookings) || location.pathname.includes(routes.salesManagement)) {
                navigate(routes.availableOrgs);
              }
            }
            dispatch(setSearchedData(data));
            if (location.pathname === routes.available_venues) {
              dispatch(setVenueList(data));
            } else {
              dispatch(setVenueList({}));
            }
            dispatch(setSelectedVenuesForCosting([]));
            removeFromStorage('selectedVenuesForCosting', true);
            removeFromStorage('orgDataForCosting', true);
            dispatch(
              setSearchFormData({
                ...body,
                startTime: formatTimeForApi(body.startTime as string),
                endTime: formatTimeForApi(body.endTime as string),
              }),
            );
            if (screenSizeDownLg) {
              setOpenModal(false);
              formRef.current?.resetForm();
            }
          }
        }
      } else {
        setHasError(true);
      }
    } catch (error: any) {
      handleCatch(error, 'error');
    }
  };

  const clearFilterFunc = () => {
    dispatch(setSearchDataSliceInitState({}));
    flushSync(() => {
      setSearchData({});
      setIsLocationDisabled(false);
    });
    formRef.current?.resetForm();
    navigate(routes.root);
  };

  const getModalWidth = () => {
    if (screenSizeDownMd) {
      if (screenSizeDownSm) return '90vw';
      else return '80vw';
    } else return '50vw';
  };

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Grid
        container
        sx={{
          justifyContent: location.pathname.includes(routes.bookings) ? 'flex-start' : 'flex-end',
        }}>
        <Grid item>
          {!location.pathname.includes(routes.bookings) &&
          !location.pathname.includes(routes.salesManagement) &&
          !location.pathname.includes(routes.viewRfq  ) ? (
            <PrimaryButton onClick={() => setOpenModal(true)}>
              <Box display="flex" alignItems={'center'} gap={1}>
                <SearchIcon />
                {searchData && Object.keys(searchData).length > 0 && (
                  <Typography>{strings.bookBanquetModifyText}</Typography>
                )}
              </Box>
            </PrimaryButton>
          ) : (
            <Tooltip title={strings.createNewBookingText}>
              {!screenSizeDownSm ? (
                <Button
                  onClick={() => setOpenModal(true)}
                  variant="contained"
                  sx={{ '.MuiButton-endIcon': { marginLeft: 0, svg: { fontSize: '18px' } }, ml: 2 }}
                  endIcon={<PostAddIcon />}
                />
              ) : (
                <PostAddIcon onClick={() => setOpenModal(true)} color="primary" />
              )}
            </Tooltip>
          )}
        </Grid>
      </Grid>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <>
          {loading && <PageLoader />}
          <Box
            sx={{
              position: 'absolute' as 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              pb: 1,
              width: getModalWidth(),
              minHeight: screenSizeDownSm ? '80vh' : '35vh',
              ':focus': { border: 0 },
            }}>
            <Box display={'flex'} alignItems="center" justifyContent={'space-between'} px={3} py={2}>
              <Typography id="modal-modal-title" variant="h5" fontWeight={600} color={'primary'}>
                {strings.checkAvailabilityText}
              </Typography>
              <ClearOutlinedIcon
                onClick={() => setOpenModal(false)}
                sx={{
                  ':hover': { cursor: 'pointer' },
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
            <Divider
              sx={{
                mx: 3,
                borderStyle: 'dashed',
                borderColor: theme.palette.secondary.main,
              }}
            />
            <Grid container columnSpacing={4} rowSpacing={1} px={3} py={1.5}>
              <Form
                dropdownData={{ location: locations }}
                hasError={hasError}
                ref={formRef as RefObject<Form>}
                model={SearchBarForm(strings, locations, eventTypes, /*timeslots, */ isLocationDisabled)}
                values={searchData}
                onChange={handleChange}
              />
              <Grid item xs={12}>
                <Grid container pb={0} pt={2} gap={1} justifyContent={'center'}>
                  <Grid item xs={12} sm={4}>
                    <PrimaryButton fullWidth onClick={(e: any) => handleSubmit(e)}>
                      {strings.searchButtonText}
                    </PrimaryButton>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <PrimaryButton fullWidth onClick={() => clearFilterFunc()} variant={'outlined'}>
                      {strings.clear_filter}
                    </PrimaryButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </>
      </Modal>
    </>
  );
};
export default SearchBar;
